import css from './timeline.module.css'
import { IMaintenanceEvent } from '@/pages/Calendar/calendar.types'
import { useEffect, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'
import maintenanceProcessesAtom from '@/pages/Processes/Maintenance/MaintenanceProcesses/maintenanceProcesses.atom'
import maintenanceBlankSchemasAtom from '@/pages/Processes/Maintenance/MaintenanceBlanks/maintenanceBlankSchemas.atom'
import TimelineElement, { ITimelineElementParams } from './components/TimelineElement'
import equipmentApi from '@/pages/Equipment/equipment.api'
import useHttpLoader from '@/shared/hooks/useHttpLoader'
import { IEquipment } from '@/pages/Equipment/equipment.types'
import ErrorIcon from '@/assets/icons/error.svg?react'
import Skeleton from '@/shared/Loaders/Skeleton'
import timelineConstants from './timeline.constants'
import timeUtils from '@/utils/time.utils'
import { IMaintenanceProcessRule } from '@/pages/Processes/Maintenance/maintenance.types'
import maintenanceUtils from '../../../maintenance.utils'
import SeparateLine from '@/shared/Forms/SeparateLine'

interface IProps {
  equipmentId: IEquipment['id']
  onElementClick: (processRuleId: IMaintenanceProcessRule['id']) => void
}

const Timeline = (props: IProps) => {
  const [maintenanceEvents, setMaintenanceEvents] = useState<IMaintenanceEvent[]>([])
  const { wait, loading } = useHttpLoader()

  const maintenanceProcesses = useRecoilValue(maintenanceProcessesAtom).items
  const blankSchemas = useRecoilValue(maintenanceBlankSchemasAtom).items

  const timelineConfig = useMemo(() => {
    const curDate = Date.now()
    const end = timeUtils.addDays(curDate, 60)
    const start = timeUtils.subDays(curDate, 29)
    const interval = timeUtils.eachDayOfInterval({ start, end })
    return { curDate, start, end, interval }
  }, [])

  useEffect(() => {
    wait(
      equipmentApi.getMaintenancePlansEquipment(props.equipmentId, {
        date_gte: timelineConfig.start.toISOString(),
        date_lte: timelineConfig.end.toISOString(),
      }),
      (resp) => {
        if (resp.status === 'success') {
          setMaintenanceEvents(resp.body)
        }
      }
    )
  }, [props.equipmentId])

  const getFormattedDate = (date: Date) => {
    const isStartOfMonth = timeUtils.isFirstDayOfMonth(date)
    const isToday = timeUtils.isToday(date)
    if (isStartOfMonth || isToday) return timeUtils.formatDate(date, 'dd.MM')
    return null
  }

  const getBlankSchema = (processId: number, processRuleId: number) => {
    const process = maintenanceProcesses.find((item) => item.id === processId)
    const processRule = process?.rules.find((item) => item.id === processRuleId)
    return blankSchemas.find((item) => item.id === processRule?.blankSchemaId)
  }

  const eventTypesByDate = useMemo(() => {
    const result: ITimelineElementParams[] = timelineConfig.interval.map((date) => ({
      date: getFormattedDate(date),
      isToday: timeUtils.isToday(date),
      isMaintenanceInterval: false,
      isMaintenanceDay: false,
      maintenanceInterval: null,
      maintenanceType: null,
      maintenanceShemaId: null,
      processRuleId: null,
      disabled: true,
      tooltipText: { mainText: timeUtils.formatDate(date, 'dd.MM.yyyy'), warning: '' },
    }))

    maintenanceEvents.forEach((item) => {
      const dayNumberInTimeline = timeUtils.differenceInCalendarDays(item.ts, timelineConfig.start)
      const { name } = getBlankSchema(item.metadata.processId, item.metadata.processRuleId)
      const isInTheFuture = new Date().getTime() < item.ts

      if (item.type === 'COMPLETED_MAINTENANCE' && dayNumberInTimeline >= 0) {
        result[dayNumberInTimeline] = {
          ...result[dayNumberInTimeline],
          processRuleId: item.metadata.processRuleId,
          isMaintenanceInterval: true,
          isMaintenanceDay: true,
          maintenanceType: item.type,
          tooltipText: {
            mainText: [timeUtils.formatDate(timelineConfig.interval[dayNumberInTimeline], 'dd.MM.yyyy'), name].join(
              '\n'
            ),
            warning: '',
          },
        }

        return
      }
      if (dayNumberInTimeline > 0) {
        const maintenanceIntervalStart = Math.max(0, dayNumberInTimeline - timelineConstants.maintenanceGracePeriod)
        const maintenanceIntervalEnd = Math.min(
          result.length - 1,
          dayNumberInTimeline + timelineConstants.maintenanceGracePeriod
        )
        const { isDateInGracePeriod, start, end } = maintenanceUtils.isDateInMaintenancePeriod(new Date(item.ts))
        const warningMessage = `Бланк обслуживания необходимо будет заполнить в любое время с ${timeUtils.formatDate(start, 'dd.MM.yyy')} по ${timeUtils.formatDate(end, 'dd.MM.yyy')}`
        const isIntervalIncludesToday = timeUtils.isWithinInterval(timelineConfig.curDate, {
          start: timeUtils.subDays(item.ts, 3),
          end: timeUtils.addDays(item.ts, 3),
        })
        for (let i = maintenanceIntervalStart; i <= maintenanceIntervalEnd; ++i) {
          result[i] = {
            ...result[i],
            processRuleId: item.metadata.processRuleId,
            isMaintenanceInterval: true,
            isMaintenanceDay: i === dayNumberInTimeline,
            maintenanceType: item.type,
            disabled: !(isIntervalIncludesToday && item.type === 'PLANNED_MAINTENANCE'),
            tooltipText: {
              mainText: [timeUtils.formatDate(timelineConfig.interval[dayNumberInTimeline], 'dd.MM.yyyy'), name].join(
                '\n'
              ),
              warning: isDateInGracePeriod || !isInTheFuture ? '' : warningMessage,
            },
          }
        }
      }
    })

    //Убираем даты, которые не вмещаются (важнее дата текущего дня)
    const curDayIndex = timeUtils.differenceInCalendarDays(timelineConfig.curDate, timelineConfig.start)
    const curDayIntervalStart = Math.max(curDayIndex - 4, 0)
    const curDayIntervalEnd = Math.min(result.length - 1, curDayIndex + 4)
    if (!result[curDayIndex].tooltipText.warning) {
      result[curDayIndex].tooltipText.warning = 'Сегодня'
    }

    for (let i = curDayIntervalStart; i <= curDayIntervalEnd; ++i) {
      if (!result[i].isToday) result[i].date = null
    }

    return result
  }, [maintenanceEvents])

  if (loading) return <Skeleton width="100%" height="49px" loading={loading} />

  if (!maintenanceEvents || maintenanceEvents.length === 0) {
    return (
      <div className={css.errorMessage}>
        <span
          className="text-caption-md inline-flex-gap center"
          style={{ color: 'var(--text-error)', textAlign: 'center' }}
        >
          <ErrorIcon />
          Генерация плана технического обслуживания невозможна. Выполните настройку, либо обратитесь к администратору.
        </span>
      </div>
    )
  }

  return (
   <div>
    <div className={css.container} style={{ gridTemplateColumns: `repeat(${timelineConfig.interval.length}, 1fr)` }}>
      {eventTypesByDate.map((item, index) => (
        <TimelineElement index={index} key={index} onElementClick={props.onElementClick} timelineElementParams={item} />
      ))}
    </div>
    <SeparateLine style={{}}/>
   </div>
  )
}

export default Timeline
